import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createBlock as _createBlock, withDirectives as _withDirectives, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, unref as _unref, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "box-row full-width" }
const _hoisted_2 = { class: "header" }
const _hoisted_3 = { class: "text-subtitle2 ellipsis" }
const _hoisted_4 = { class: "text-body2 ellipsis" }
const _hoisted_5 = {
  key: 0,
  class: "q-mt-xs"
}
const _hoisted_6 = { class: "text-grey-7 q-mt-sm ellipsis text-caption" }
const _hoisted_7 = {
  key: 1,
  class: "text-body3 text-grey-7 ellipsis"
}
const _hoisted_8 = { class: "text-grey-7 ellipsis text-caption" }
const _hoisted_9 = {
  key: 2,
  class: "q-mt-sm"
}

import { formatTimeAgo } from "@vueuse/core";
  import { computed, onMounted, ref, PropType, watch} from "vue";
  import { useRouter } from "vue-router";

  import VehicleAvatar from "src/components/vehicle/VehicleAvatar.vue";

  import { LoanStatusType_Enum } from "src/types/enums/index";
  import { LoanApplication, Vehicle } from "src/types/loan-application";
  import { QuickQuote } from "src/types/quote";

  import { useDifferenceInMinutes, useDifferenceInDays, useCapitalise, useSanitiseReg } from "src/functions";

  import { useProposalStore } from "src/stores/proposal-store";
  import { useSettingsStore } from "src/stores/settings-store";
  import { useAuthStore } from "../../../stores/auth-store";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'ApplicationOverview',
  props: {
    application: {
      type: Object as PropType<LoanApplication>,
      default: null
    }
  },
  setup(__props) {

  const router = useRouter();
  const proposalStore = useProposalStore();
  const settingsStore = useSettingsStore();
  const authStore = useAuthStore();

  const props = __props

  const isMobile = computed(() => settingsStore.isMobile);

  const isQuickQuote = computed(() => props.application.isQuickQuote);

  function stateLabel(status: number) {
    return LoanStatusType_Enum[status];
  }

  function customerName() {
    return props.application.customer ? useCapitalise(props.application.customer.firstName) + " " + useCapitalise(props.application.customer.lastName) : 'No Customer Provided';
  }

  function vehicleString() {
    return props.application.vehicle ? (useCapitalise(props.application.vehicle.make) + " " + useCapitalise(props.application.vehicle.model)) : 'No Vehicle Provided';
  }

  function registration() {
    return props.application.vehicle ? useSanitiseReg(props.application.vehicle.vehicleRegistration) : null
  }

  const dealerName = computed(() => {
    if (!authStore.selectedDealers) {
      return null;
    }

    const d = authStore.selectedDealers.find(d => d.affiliateUserId === Number(props.application.affiliateUserId));

    if (!d) {
      return null;
    }

    const split = d.name.replace("t/a", "T/A").split(" T/A ");

    return split.length === 1 ? split[0] : split[1];
  })

  const vehicle = computed(() => props.application.isQuickQuote ? { vehicleId: props.application.vehicle.vehicleId  } as Vehicle : props.application.vehicle)

  function viewApplication(event: MouseEvent) {
  
    props.application.isQuickQuote ?
      router.push({ path: `/quick-quote/${props.application.quickQuoteId}` }) :
      router.push({ path: `/customer/${props.application.brokerReference > 0 ? props.application.brokerReference : props.application.cosmosId}` })
  }

  function mousedown(event: MouseEvent) {
    event.stopPropagation();
  }

  function sanitiseName(name: string) {
    if (name === "Carfinance.Application.Form.Prototype") {
      return "Customer application";
    }
    return name;
  }

  const vehicleValid = computed(() => {

    if ([
      LoanStatusType_Enum["NTU Rate Too High"],
      LoanStatusType_Enum["NTU Lost To Competitor"],
      LoanStatusType_Enum["NTU Outside Criteria"],
      LoanStatusType_Enum["NTU Other"],
      LoanStatusType_Enum["NTU No Contact"]
    ].includes(props.application.state)) {
      return 0;
    }

    if (!props.application || !props.application.vehicle || props.application.isQuickQuote) {
      return 0;
    }

    if (!props.application || !props.application.proposals || props.application.proposals.length === 0) {
      return 0;
    }

    if (props.application.vehicle && props.application.vehicle.cannotPrice) {
      return 2;
    }

    return 3;
  })

  const decisionStatus = computed(() => {

    // Complete
    if (props.application.state === LoanStatusType_Enum.Completed) {
      return {
        label: "Completed",
        colour: "bg-positive text-white"
      };
    }

    // NTU 
    if ([
      LoanStatusType_Enum["NTU Rate Too High"],
      LoanStatusType_Enum["NTU Lost To Competitor"],
      LoanStatusType_Enum["NTU Outside Criteria"],
      LoanStatusType_Enum["NTU Other"],
      LoanStatusType_Enum["NTU No Contact"]
    ].includes(props.application.state)) {
      return {
        label: `${LoanStatusType_Enum[props.application.state]}`,
        colour: "bg-warning text-dark"
      };
    }

    // Expired
    if (useDifferenceInDays(props.application.created, new Date()) > 45) {
      return {
        label: "Proposals Expired",
        colour: "bg-warning"
      };
    }

    if (props.application.proposals && props.application.proposals.length > 0) {
      // Approved / Vehicle Failed
      if (props.application.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus.toString() === 'Accepted')) {
        return {
          label: [3, 0].includes(vehicleValid.value) ? "Approved" : "Failed Vehicle Rule",
          colour: [3, 0].includes(vehicleValid.value) ? "bg-positive text-white" : "bg-warning"
        }
      }

      // Provisional Approval / Vehicle Failed
      if (props.application.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus.toString() === 'ProvisionalAcceptance' && (!p.customerRuleResults || (p.customerRuleResults && p.customerRuleResults.find(r => r.resultType === "FixedDecline"))))) {
        return {
          label: [3, 0].includes(vehicleValid.value) ? "Approved (Predicted)" : "Failed Vehicle Rule",
          colour: [3, 0].includes(vehicleValid.value) ? "bg-positive  text-white" : "bg-warning"
        }
      }

      // Conditional Approval / Vehicle Failed
      if (props.application.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus.toString() === 'ConditionallyAccepted' && (!p.customerRuleResults || (p.customerRuleResults && p.customerRuleResults.find(r => r.resultType === "FixedDecline"))))) {
        return {
          label: [3, 0].includes(vehicleValid.value) ? "Approved (Conditionally)" : "Failed Vehicle Rule",
          colour: [3, 0].includes(vehicleValid.value) ? "bg-positive  text-white" : "bg-warning"
        }
      }

      // Processing
      if (props.application.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus.toString() === 'Processing')) {
        return {
          label: "Awaiting Decision",
          colour: "bg-warning"
        }
      }

      // Declined
      if (props.application.proposals.find(p => p.loanProposalStatus && p.loanProposalStatus.toString() === 'Declined') ||
      props.application.proposals.find(p => (p.customerRuleResults && p.customerRuleResults.find(r => r.resultType === 'FixedDecline')) || (p.loanProposalStatus && p.loanProposalStatus.toString() === 'FailedSendRules'))) {
        return {
          label: "Declined",
          colour: "bg-negative text-white"
        }
      }
    }
    else {
      // App just created
      if (useDifferenceInMinutes(new Date(props.application.created), new Date()) < 15) {
        return {
          label: "Awaiting Decision",
          colour: "bg-warning text-dark"
        };
      }


      if (!props.application.proposals || !props.application.proposals.length) {
        return null
      }
    }
  })

  

  onMounted(() => {
    if (!props.application.isQuickQuote) {
      getProposals(true);
    }
  })

  async function getProposals(skip: boolean = false) {
    const mins = useDifferenceInMinutes(new Date(props.application.created), new Date());
    const days = useDifferenceInDays(new Date(props.application.created), new Date());

    // less than 15 mins old = 1 min poll
    // 15 mins - 1 day old = 1 hr poll
    // 1 day+ - 1 day poll
    let retryIn = days > 1 ? (1000 * 60 * 60 * 24) : mins < 15 ? 60000 : (1000 * 60 * 60);

    if (!skip && props.application) {
      await proposalStore.getAvailableProposalsForApplication(props.application, false).catch(error => { console.log("Unable to get proposals", error) });;
    }

    setTimeout(() => {
      getProposals();
    }, retryIn)
  }

  const hasRecentSocketMessage = computed(() => props.application.lastSocketUpdate && (new Date().getTime() - props.application.lastSocketUpdate < 60000) ? true : false)
  watch(() => hasRecentSocketMessage.value, (newVal: boolean, oldVal: boolean) => {

  })

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_list = _resolveComponent("q-list")!
  const _component_q_menu = _resolveComponent("q-menu")!
  const _component_q_badge = _resolveComponent("q-badge")!
  const _component_q_card = _resolveComponent("q-card")!
  const _directive_ripple = _resolveDirective("ripple")!
  const _directive_close_popup = _resolveDirective("close-popup")!

  return (_openBlock(), _createBlock(_component_q_card, {
    bordered: "",
    style: {"border-radius":"5px"},
    clickable: "",
    class: _normalizeClass(["cursor-pointer", hasRecentSocketMessage.value ? 'update-ripple' : '']),
    onClick: _cache[1] || (_cache[1] = ($event: any) => (viewApplication($event))),
    onMousedown: _withModifiers(mousedown, ["prevent"])
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_menu, { "context-menu": "" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_list, null, {
            default: _withCtx(() => [
              _withDirectives((_openBlock(), _createBlock(_component_q_item, {
                clickable: "",
                onClick: _cache[0] || (_cache[0] = ($event) => viewApplication($event as MouseEvent))
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_q_item_section, { side: "" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_q_icon, {
                        name: "mdi-eye",
                        size: "xs"
                      })
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_q_item_section, null, {
                    default: _withCtx(() => _cache[2] || (_cache[2] = [
                      _createTextVNode(" View ")
                    ])),
                    _: 1,
                    __: [2]
                  })
                ]),
                _: 1
              })), [
                [_directive_ripple],
                [_directive_close_popup]
              ])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      (!isQuickQuote.value && decisionStatus.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["q-px-sm q-py-xs text-overline text-uppercase", decisionStatus.value.colour])
          }, _toDisplayString(decisionStatus.value.label), 3))
        : _createCommentVNode("", true),
      _createElementVNode("div", {
        class: _normalizeClass(isMobile.value ? 'q-pa-md' : 'q-pa-sm')
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _createVNode(VehicleAvatar, {
              vehicle: vehicle.value,
              size: "60px",
              approved: vehicleValid.value
            }, null, 8, ["vehicle", "approved"])
          ]),
          _createElementVNode("div", {
            class: _normalizeClass(["content overflow-hidden", isMobile.value ? 'q-pl-md' : 'q-pl-md'])
          }, [
            _createElementVNode("div", _hoisted_3, _toDisplayString(customerName()), 1),
            _createElementVNode("div", _hoisted_4, _toDisplayString(vehicleString()), 1),
            (registration())
              ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                  _createVNode(_component_q_badge, {
                    color: "reg-yellow",
                    class: "text-dark text-reg text-uppercase text-subtitle2",
                    style: {"padding":"1px 6px 1px 6px"}
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(registration()), 1)
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_6, _toDisplayString(sanitiseName(__props.application.createdBy)), 1),
            (_unref(authStore).selectedDealers && _unref(authStore).selectedDealers.length > 1)
              ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(dealerName.value), 1))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_8, " Updated " + _toDisplayString(_unref(formatTimeAgo)(new Date(__props.application.isQuickQuote ? __props.application.created : __props.application.lastUpdated))), 1),
            (isMobile.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                  _createVNode(_component_q_badge, {
                    class: "bg-light-blue text-dark text-overline text-uppercase",
                    style: {"padding":"4px 8px 4px 8px"}
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(stateLabel(__props.application.state)), 1)
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ], 2)
        ])
      ], 2)
    ]),
    _: 1
  }, 8, ["class"]))
}
}

})